import { type MeetingResultsCubit } from '@breakoutlearning/firebase-repository/cubits/MeetingResultsCubit'
import type { PublicUser } from '@breakoutlearning/firebase-repository/models/PublicUser'
import type { RoomStateRubricResultDetail } from '@breakoutlearning/firebase-repository/models/RoomStateRubricResultDetail'
import type { SlideRubric } from '@breakoutlearning/firebase-repository/models/SlideRubric'
import { BreakoutUserAvatar } from 'components/breakout/BreakoutUserAvatar'
import { BreakoutButton } from 'components/design-system/BreakoutButton'
import { BreakoutTooltip } from 'components/design-system/BreakoutTooltip'
import { useRepository } from 'hooks/auth'
import { useMeetingResultsCubit } from 'hooks/cubits/meetingsResults'
import { useBreakoutUser } from 'hooks/profile'
import { observer } from 'mobx-react-lite'
import * as p2 from 'p2-es'
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { getColor } from 'util/color'

type ChartCircle = {
  body: p2.Body
  shape: p2.Circle
  detail: RoomStateRubricResultDetail
}

type ChartPlane = {
  body: p2.Body
  shape: p2.Plane
}

type ChartBox = {
  body: p2.Body
  shape: p2.Box
}

function buildGround({
  position,
  mass,
  angle,
}: {
  position: number[]
  mass?: number
  angle?: number
}) {
  const planeShape = new p2.Plane()
  const planeBody = new p2.Body({
    position,
    mass,
    angle,
  })
  planeBody.addShape(planeShape)

  const planeWithSvg: ChartPlane = {
    body: planeBody,
    shape: planeShape,
  }

  return planeWithSvg
}

// A channel is two planes with a space in between
function buildChannel({ x, width }: { x: number; width: number }) {
  // left wall
  const leftPlane = new p2.Plane()
  const leftBody = new p2.Body({
    position: [x, 0],
    angle: -Math.PI / 2,
  })
  leftBody.addShape(leftPlane)

  // right wall
  const rightPlane = new p2.Plane()
  const rightBody = new p2.Body({
    position: [x + width, 0],
    angle: Math.PI / 2,
  })
  rightBody.addShape(rightPlane)

  return {
    leftPlane,
    leftBody,
    rightPlane,
    rightBody,
  }
}

function buildCircle({
  radius,
  x,
  y,
  angularVelocity,
  mass,
  detail,
}: {
  radius: number
  x: number
  y: number
  angularVelocity?: number
  mass?: number
  color?: string
  description?: string
  detail: RoomStateRubricResultDetail
}) {
  // Add a circle
  const circleShape = new p2.Circle({ radius })
  const circleBody = new p2.Body({
    mass: mass || 1,
    position: [x, y],
    angularVelocity: angularVelocity || 0,
  })
  circleBody.addShape(circleShape)

  const circleWithSvg: ChartCircle = {
    body: circleBody,
    shape: circleShape,
    detail,
  }

  return circleWithSvg
}

function buildWorldForBucket({
  data,
  scaleData,
}: {
  data: RoomStateRubricResultDetail[]
  scaleData: ReturnType<MeetingResultsCubit['generateConversationMapScaleData']>
}) {
  const circles: ChartCircle[] = []
  const planes: ChartPlane[] = []
  const walls: ChartBox[] = []

  const gravity = -2500

  const world = new p2.World({ gravity: [0, gravity] })
  const material = new p2.Material()
  world.defaultMaterial = material
  world.defaultContactMaterial = new p2.ContactMaterial(material, material, {
    restitution: 0.00000001,
    friction: 0.1,
    stiffness: 1e6 * 10,
    // p2.Equation.DEFAULT_STIFFNESS,
    // relaxation: p2.Equation.DEFAULT_RELAXATION,
    // frictionStiffness: 1e6,
    // frictionRelaxation: 0.1,
    // surfaceVelocity: 0,
  })

  const width = 500

  data?.map((item, index2) => {
    const score = item.data.score
    const x = width / 2
    const jitter = -10 + Math.random() * 20
    // 36 is actually in meters :), not pixels
    const size = scaleData.startSize + score * scaleData.scoreMultiplier
    const circle = buildCircle({
      radius: size,
      x: x + jitter,
      y: 1200 + index2 * size * 3,
      mass: 2,
      detail: item,
    })
    world.addBody(circle.body)
    circles.push(circle)
  })

  const ground = buildGround({
    position: [0, 0],
  })

  world.addBody(ground.body)
  planes.push(ground)

  // channel controls the left and right boundaries
  const channel = buildChannel({ x: 0, width: width })
  world.addBody(channel.leftBody)
  world.addBody(channel.rightBody)

  return {
    world,
    circles,
    planes,
    walls,
  }
}

const ConversationMapAnimatedGraphBucketed = observer(
  function ConversationMapAnimatedGraphBucketed({
    data,
  }: {
    data: {
      startTimeTS: number
      endTimeTS: number
      buckets: RoomStateRubricResultDetail[][]
      minutesPerBucket: number
    }
  }) {
    const currentUser = useBreakoutUser()
    const isAdmin = currentUser.isAdmin
    const cubit = useMeetingResultsCubit()
    const userId = cubit.userId
    const [key, setKey] = useState<number>(0)
    const { t } = useTranslation()

    const restart = useCallback(() => {
      setKey((prev) => prev + 1)
    }, [])

    const buckets = data.buckets

    const repository = useRepository()
    const colorsByUserId = useMemo(() => {
      // deterministically assign colors to users
      const availableColors = [
        '#FFCA00',
        '#000000',
        '#BFBFBF',
        '#737373',
        '#FFE875',
        '#D2922D',
        '#E5E3CC',
      ]
      const colors: Record<string, string> = {}
      buckets.forEach((bucket) => {
        if (!bucket) return
        bucket.forEach((item) => {
          if (!colors[item.data.userId]) {
            const color = availableColors.shift()
            colors[item.data.userId] = color || getColor(item.data.userId)
          }
        })
      })
      return colors
    }, [buckets])

    return (
      <div className="w-full overflow-auto">
        {isAdmin && false && (
          <BreakoutButton onClick={restart}>Restart</BreakoutButton>
        )}

        <div className="mb-2 flex flex-row gap-2">
          {Object.keys(colorsByUserId).map((userId) => {
            const {
              data: { firstName },
            } =
              cubit.meetingCubit.users.find((u) => u.id === userId) ||
              repository.userStore.getUser(userId)
            return (
              <div key={userId} className="flex flex-row items-center gap-1">
                <div
                  className="h-3 w-3 overflow-hidden rounded-full border-2 p-0"
                  style={{
                    borderColor: colorsByUserId[userId],
                  }}
                >
                  <div
                    className="h-4 w-4"
                    style={{
                      backgroundColor: colorsByUserId[userId],
                      borderColor: colorsByUserId[userId],
                    }}
                  ></div>
                </div>
                <div className="text-label-small">{firstName}</div>
              </div>
            )
          })}
        </div>

        <table role="presentation" className="w-full">
          <tbody>
            <tr>
              <td className="w-2 overflow-hidden" rowSpan={2}>
                <div className="flex max-w-6 flex-col-reverse items-center whitespace-nowrap pr-4">
                  <strong className="ml-3 -rotate-90 transform text-label-small text-grey-text">
                    {t('meeting.comments_shared')}
                  </strong>
                </div>
              </td>
              <td>
                <AnimatedCirclesMultiBucket
                  key={key}
                  buckets={data.buckets}
                  startTimeTS={data.startTimeTS}
                  endTimeTS={data.endTimeTS}
                  userId={userId}
                  colorsByUserId={colorsByUserId}
                />
              </td>
            </tr>
            <tr>
              <td colSpan={2} className="py-2 text-center">
                <strong className="ml-3 -rotate-90 transform text-label-small text-grey-text">
                  {t('meeting.timeline')}
                </strong>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    )
  }
)

const AnimatedCirclesMultiBucket = observer(
  function AnimatedCirclesMultiBucket({
    buckets,
    userId,
    startTimeTS,
    endTimeTS,
    colorsByUserId,
  }: {
    buckets: RoomStateRubricResultDetail[][]
    userId: string
    startTimeTS: number
    endTimeTS: number
    colorsByUserId: Record<string, string>
  }) {
    const ref = useRef<HTMLDivElement>(null)
    const cubit = useMeetingResultsCubit()
    const [realBucketWidth, setRealBucketWidth] = useState(1)

    const scaleData = useMemo(
      () => cubit.generateConversationMapScaleData(buckets, realBucketWidth),
      [cubit, buckets, realBucketWidth]
    )

    useLayoutEffect(() => {
      if (!ref.current) return
      const { current } = ref
      const bucketCount = buckets.length
      const observer = new ResizeObserver(() => {
        const { width } = current.getBoundingClientRect()
        setRealBucketWidth(width / bucketCount)
      })
      observer.observe(ref.current)
      return () => observer.unobserve(current)
    }, [buckets.length])

    return (
      <>
        <div className="flex flex-row" ref={ref}>
          {buckets.map((bucket, index) => {
            const duration = endTimeTS - startTimeTS
            const bucketTimestamp = duration / buckets.length / 2
            const thisBucketTimestamp =
              index * bucketTimestamp + (index + 1) * bucketTimestamp
            return (
              <AnimatedCirclesBucket
                key={index}
                bucket={bucket}
                userId={userId}
                colorsByUserId={colorsByUserId}
                startTimeTS={startTimeTS}
                timestamp={thisBucketTimestamp}
                scaleData={scaleData}
                // scaleData={{
                //   startSize: 30,
                //   scoreMultiplier: 10,
                //   parentHeight: 200
                // }}
              />
            )
          })}
        </div>
      </>
    )
  }
)

function secondsToTime(milliseconds: number) {
  const seconds = Math.floor(milliseconds / 1000)
  const minutes = Math.floor(seconds / 60)
  let remainingSeconds: string | number = Math.floor(seconds % 60)
  if (remainingSeconds < 10) {
    remainingSeconds = '0' + remainingSeconds
  }
  return `${minutes}:${remainingSeconds}`
}

const AnimatedCirclesBucket = observer(function StaticCirclesGraph({
  bucket,
  userId,
  colorsByUserId,
  timestamp,
  startTimeTS,
  scaleData,
}: {
  bucket: RoomStateRubricResultDetail[]
  userId: string
  colorsByUserId: Record<string, string>
  timestamp: number
  startTimeTS: number
  scaleData: ReturnType<MeetingResultsCubit['generateConversationMapScaleData']>
}) {
  const cubit = useMeetingResultsCubit()
  const repository = useRepository()

  // build world for bucket calcs the circle size
  const world = useMemo(() => {
    return buildWorldForBucket({ data: bucket, scaleData })
  }, [bucket, scaleData])

  const [data, setData] = useState<{
    circles: ChartCircle[]
    planes: ChartPlane[]
    walls: ChartBox[]
  }>({
    circles: [],
    planes: [],
    walls: [],
  })

  // TODO: remove this if not needed
  if (userId) {
    // noop
  }
  useEffect(() => {
    let lastTime: number | null = null
    let count = 0
    const maxSubSteps = 5 // Max physics ticks per render frame
    const fixedDeltaTime = 1 / 60 // Physics "tick" delta time
    const scheduleNextFrame = () => {
      requestAnimationFrame(animate)
    }
    const animate = (time: number) => {
      // if (!world) return
      // Get the elapsed time since last frame, in seconds
      let deltaTime = lastTime ? (time - lastTime) / 1000 : 0
      lastTime = time

      // Make sure the time delta is not too big (can happen if user switches browser tab)
      deltaTime = Math.min(1 / 10, deltaTime)

      // Move physics bodies forward in time
      world.world.step(fixedDeltaTime, deltaTime, maxSubSteps)

      setData({
        circles: world.circles,
        planes: world.planes,
        walls: world.walls,
      })

      // stop the animation after 10 seconds
      // there is no more movement after the shapes drop
      // so not point in continuing the calculations
      const limit = 10 * 60
      if (count > limit) return
      count++
      scheduleNextFrame()
    }
    scheduleNextFrame()
  }, [world])

  return (
    <div
      className="flex flex-1 flex-col"
      style={{
        height: scaleData.parentHeight,
      }}
    >
      {/* heigh: 166 = parentHeight - 33 (bottom label)*/}
      {/* width: dynamic based on view width interrogate width from ref*/}
      <div className="flex h-full flex-1  items-end overflow-hidden border-r  ">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 500 2000"
          className="w-full"
        >
          {data.circles.map((circle, index) => {
            const item = circle.detail
            const {
              data: { eventTime, content },
            } = item
            const rubric = cubit.rubricsById[circle.detail.data.rubricId]
            const itemUser =
              cubit.meetingCubit.users.find((u) => u.id === item.data.userId) ||
              repository.userStore.getUser(item.data.userId)
            const timeSinceStart = item.data.eventTime.getTime() - startTimeTS
            const minutesSinceStart = secondsToTime(timeSinceStart)
            const tooltipEnabled =
              repository.featureFlags.data
                .sessionResultsShowAllConversationTooltips ||
              cubit.userId === item.data.userId
            return (
              <CircleWithAnalyticsEvent
                index={index}
                key={index}
                circle={circle}
                fill={colorsByUserId[item.data.userId]}
                stroke={colorsByUserId[item.data.userId]}
                cubit={cubit}
                user={itemUser}
                rubric={rubric}
                content={content}
                minutesSinceStart={minutesSinceStart}
                eventTime={eventTime}
                tooltipEnabled={tooltipEnabled}
              />
            )
          })}
        </svg>
      </div>
      <div className="p-2 text-center text-body-medium">
        {secondsToTime(timestamp)}
      </div>
    </div>
  )
})

const CircleWithAnalyticsEvent = ({
  circle,
  index,
  stroke,
  fill,
  cubit,
  tooltipEnabled,
  user,
  rubric,
  content,
  minutesSinceStart,
  eventTime,
}: {
  circle: ChartCircle
  index: number
  stroke: string
  fill: string
  cubit: MeetingResultsCubit
  tooltipEnabled: boolean
  user: PublicUser
  rubric: SlideRubric
  content: string
  minutesSinceStart: string
  eventTime: Date
}) => {
  const hoverTimeout = useRef<NodeJS.Timeout>()
  const { useMockResults } = cubit.meetingCubit

  useEffect(() => {
    const { current } = hoverTimeout
    return () => clearTimeout(current)
  }, [])

  const handleMouseEnter = () => {
    const timeout = setTimeout(
      (cubit) => {
        cubit.repository.logEvent('conversation_map_hover', {
          roomId: cubit.roomStateId,
        })
      },
      750,
      cubit
    ) // send event after 750ms
    hoverTimeout.current = timeout
  }

  const handleMouseLeave = () => {
    clearTimeout(hoverTimeout.current)
    hoverTimeout.current = undefined
  }

  return (
    <BreakoutTooltip
      enabled={tooltipEnabled}
      key={index}
      placement={'right-start'}
      content={
        <div className="max-w-[500px] rounded-2xl border border-outline-variant bg-white p-6 py-4">
          <div className="flex flex-row justify-between">
            <div className="mb-1 flex flex-row gap-2 text-label-small text-on-surface ">
              <BreakoutUserAvatar radius={8} user={user} />
              <span>{user.fullName}</span>
            </div>
            <div className="text-right text-body-small text-on-surface-var">
              {minutesSinceStart || eventTime.toLocaleString()}
            </div>
          </div>
          <div className="flex flex-row justify-between">
            <div className="mb-1 gap-2 text-label-small text-on-surface">
              {rubric?.data.rubricDescription}
            </div>
          </div>
          <div className="mb-1 text-body-small text-on-surface-var">
            {content}
          </div>
        </div>
      }
    >
      <circle
        key={index}
        cx={circle.body.position[0]}
        cy={1999 - circle.body.position[1]}
        r={circle.shape.radius}
        fill={fill}
        stroke={stroke}
        strokeWidth={2}
        onMouseEnter={!useMockResults ? handleMouseEnter : undefined}
        onMouseOut={!useMockResults ? handleMouseLeave : undefined}
      />
    </BreakoutTooltip>
  )
}

// eslint-disable-next-line import/no-default-export
export default ConversationMapAnimatedGraphBucketed
